/**
 * Conversion event tracking.
 *
 * GA4 recorded no key events at all: across 90 days and 942 organic sessions the
 * property held 1,412 page_view, 203 scroll, 20 click and a single form_start. There
 * was no way to tell whether any SEO or content change produced a lead.
 *
 * Events are pushed to the GTM dataLayer (GTM-K32642S2, initialised in
 * components/Firebase/firebaseApp.js), which forwards them to GA4 property
 * G-KJF7MPDMT5.
 *
 * NOTE: pushing the event is only half the job. Each event below must also be marked
 * as a key event in GA4 → Admin → Events before it counts as a conversion. See the
 * KEY_EVENTS list for the exact names to mark.
 */

import { isPrerender } from './isPrerender';

// Mark these as key events in GA4 Admin → Events.
export const KEY_EVENTS = [
  'generate_lead', // contact form submitted
  'book_meeting', // Calendly booking link opened
  'contact_email', // mailto: link clicked
];

const isBrowser = () => typeof window !== 'undefined';

export function trackEvent(eventName, params = {}) {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });
  // The same conversions in Ahrefs' Events panel, so its conversion rate means
  // something instead of sitting at zero. No extra call sites to keep in sync.
  sendAhrefsEvent(eventName, params);
}

/**
 * Document-level click delegation for mailto: and outbound links.
 *
 * Delegation rather than per-link handlers because contact addresses are scattered
 * across containers (Products, Consult, Footer, …) and Cloudflare rewrites some of
 * them to /cdn-cgi/l/email-protection at serve time, so the href is not always a
 * literal mailto: in the DOM.
 */
export function trackOutboundClicks() {
  if (!isBrowser()) return () => {};

  const onClick = (event) => {
    const link = event.target.closest?.('a');
    if (!link) return;

    const href = link.getAttribute('href') || '';

    if (href.startsWith('mailto:') || href.includes('/cdn-cgi/l/email-protection')) {
      trackEvent('contact_email', { link_url: href, link_text: link.innerText?.trim() });
      return;
    }

    if (href.includes('calendly.com')) {
      trackEvent('book_meeting', { link_url: href });
      return;
    }

    // Outbound links generally — App Store, LinkedIn, YouTube, Discord.
    if (/^https?:\/\//.test(href) && !href.includes('techlara.se')) {
      trackEvent('outbound_click', { link_url: href, link_text: link.innerText?.trim() });
    }
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}


/* ---------------------------------------------------------------------------
 * Ahrefs Web Analytics
 *
 * The tag in public/index.html carries data-no-pageview-auto, so the tracker
 * sends nothing by itself and everything below drives it instead.
 *
 * Why: left to itself the tracker patches history.pushState and fires the
 * pageview *synchronously from inside it* — before React commits and before
 * react-helmet writes the new <title>. Every in-app navigation was therefore
 * logged with the *previous* page's title. It also never patches replaceState,
 * so the language switch on an article (container/News/News.jsx) and the
 * <Navigate replace> in Apps/AppLanding produced no pageview at all.
 *
 * Trade-off: a visitor whose JS bundle fails now produces no pageview, where
 * the standalone tag would still have logged the entry page.
 * ------------------------------------------------------------------------- */

const AHREFS_GLOBAL = 'AhrefsAnalytics';
const AHREFS_OPTOUT_KEY = 'ahrefsOptOut';
const AHREFS_QUEUE_LIMIT = 20;

// The tag is async, so the global can still be missing when the first pageview
// fires. Hold events until the tracker announces itself.
const ahrefsQueue = [];

const ahrefsApi = () => (isBrowser() ? window[AHREFS_GLOBAL] : undefined);

function flushAhrefsQueue() {
  const api = ahrefsApi();
  if (typeof api?.sendEvent !== 'function') return;
  while (ahrefsQueue.length) {
    const [name, options] = ahrefsQueue.shift();
    api.sendEvent(name, options);
  }
}

if (isBrowser() && !isPrerender) {
  document.addEventListener(`${AHREFS_GLOBAL}-ready`, flushAhrefsQueue);
}

/**
 * Keep your own visits out of the numbers: open any page once with ?ahrefs=off
 * in each browser you use, and ?ahrefs=on to start counting yourself again.
 * Ahrefs only auto-excludes localhost, so visits to the live site are counted
 * as ordinary traffic without this.
 */
function ahrefsOptedOut() {
  try {
    const flag = new URLSearchParams(window.location.search).get('ahrefs');
    if (flag === 'off') localStorage.setItem(AHREFS_OPTOUT_KEY, '1');
    if (flag === 'on') localStorage.removeItem(AHREFS_OPTOUT_KEY);
    return localStorage.getItem(AHREFS_OPTOUT_KEY) === '1';
  } catch (error) {
    return false; // Private mode: storage throws, so just count the visit.
  }
}

// Ahrefs JSON-stringifies props as given; keep them flat strings and drop empties.
function ahrefsProps(params) {
  const props = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    props[key] = String(value);
  });
  return props;
}

export function sendAhrefsEvent(eventName, params = {}) {
  if (!isBrowser() || isPrerender || ahrefsOptedOut()) return;

  const props = ahrefsProps(params);
  const options = Object.keys(props).length ? { props } : undefined;
  const api = ahrefsApi();

  if (typeof api?.sendEvent === 'function') {
    api.sendEvent(eventName, options);
  } else if (ahrefsQueue.length < AHREFS_QUEUE_LIMIT) {
    ahrefsQueue.push([eventName, options]);
  }
}

// react-helmet writes the title in an effect that runs after the route's lazy
// chunk has loaded and committed — hundreds of ms after the URL changed, and
// occasionally in two steps. Send once the title has stopped changing, or at
// the hard cap, whichever comes first.
const TITLE_QUIET_MS = 150;
const TITLE_MAX_WAIT_MS = 1500;

let lastPageviewHref = null;
let titleObserver = null;
let quietTimer = null;
let maxWaitTimer = null;

function stopWaitingForTitle() {
  clearTimeout(quietTimer);
  clearTimeout(maxWaitTimer);
  titleObserver?.disconnect();
  titleObserver = null;
}

function sendAhrefsPageview() {
  stopWaitingForTitle();
  const { href } = window.location;
  if (href === lastPageviewHref) return;
  lastPageviewHref = href;
  sendAhrefsEvent('pageview');
}

/**
 * One pageview for the current URL, with the title the visitor actually saw.
 * Called on every route change by components/AhrefsPageviews.
 *
 * A navigation that is superseded within TITLE_QUIET_MS (a redirect hop such as
 * /apps/:unknown -> /products) is dropped rather than counted: nobody read it.
 */
export function trackAhrefsPageview() {
  if (!isBrowser() || isPrerender) return;

  stopWaitingForTitle();
  if (window.location.href === lastPageviewHref) return;

  // First load: the title is already in the prerendered HTML, so don't sit on
  // the pageview for a second and a half waiting for a change that won't come.
  const titleEl = document.querySelector('title');
  if (lastPageviewHref === null || !titleEl || typeof MutationObserver === 'undefined') {
    sendAhrefsPageview();
    return;
  }

  maxWaitTimer = setTimeout(sendAhrefsPageview, TITLE_MAX_WAIT_MS);
  titleObserver = new MutationObserver(() => {
    clearTimeout(quietTimer);
    quietTimer = setTimeout(sendAhrefsPageview, TITLE_QUIET_MS);
  });
  titleObserver.observe(titleEl, { childList: true, characterData: true, subtree: true });
}
